import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { View, Text, StyleSheet, RefreshControl, ActivityIndicator, NativeSyntheticEvent, NativeScrollEvent, InteractionManager, Animated } from "react-native";
import { ScrollView } from "react-native-virtualized-view";
import { SearchBar } from "../components/browse/SearchBar";
import { useNavigation, useRoute } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { RootStackParamList } from "../types/navigation";
import { VendorShowcase } from "../components/vendors/VendorShowcase";
import { spacing, typography } from "../theme";
import { useColors, ThemeColors } from "../hooks/useColors";
import { ProductCategoryList } from "../components/products/ProductCategoryList";
import { ProductFilterPanel } from "../components/products/ProductFilterPanel";
import { ProductGrid } from "../components/products/ProductGrid";
import { useProducts } from "../hooks/useProducts";
import { useAuth } from "../hooks/useAuth";
// TODO: Switch back to useProductsPaginated once vendor.status filter bug is fixed
// import { useProductsPaginated } from "../hooks/useProductsPaginated";
import { LoadingState } from "../components/ui/LoadingState";
import { EmptyState } from "../components/ui/EmptyState";
import type { Filters } from "../types/filters";
import type { RouteProp } from "@react-navigation/native";
import { OngoingOrdersDisplay } from "../components/orders/OngoingOrdersDisplay";
import { appEvents } from "../utils/orderEvents";
import { useRealNotifications } from "../hooks/useRealNotifications";
import { logger } from "../utils/logger";
import { useGuestMode } from "../contexts/GuestModeContext";

// ---------- helpers ----------
function shallowEqual(a: any, b: any) {
  if (a === b) return true;
  if (!a || !b) return false;
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  for (const k of ak) if (a[k] !== b[k]) return false;
  return true;
}

function sanitize<T extends Record<string, any>>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const k in obj) {
    const v = obj[k];
    if (v !== undefined && v !== null) (out as any)[k] = v;
  }
  return out;
}

// ---------- types ----------
type NavigationProp = NativeStackNavigationProp<RootStackParamList>;
type HomeScreenRouteProp = RouteProp<RootStackParamList, "Home">;

export default function HomeScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<HomeScreenRouteProp>();
  const colors = useColors();
  const styles = createStyles(colors);
  const { fetchNotifications } = useRealNotifications();
  const { user } = useAuth();
  const { isGuestMode } = useGuestMode();
  
  // Animation values - start at final values to prevent dimming issues during hot reload
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const slideAnim = useRef(new Animated.Value(0)).current;

  // Delay rendering OngoingOrdersDisplay only on INITIAL mount
  // This prevents the cascade of effects from blocking touches on first load
  // Once shown, it stays visible - no need to hide/show on every focus
  const [canShowOrders, setCanShowOrders] = useState(false);
  const hasShownOrdersRef = useRef(false);
  
  // Only delay on initial mount, not on every focus
  useEffect(() => {
    // If we've already shown orders once, keep them visible
    if (hasShownOrdersRef.current) {
      setCanShowOrders(true);
      return;
    }
    
    // Initial mount delay to ensure touch responsiveness
    const timeout = setTimeout(() => {
      setCanShowOrders(true);
      hasShownOrdersRef.current = true;
    }, 1000);
    
    return () => clearTimeout(timeout);
  }, []);

  // Initialize searchQuery with route params if available
  const initialSearchQuery = route.params?.searchQuery || "";
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);

  // Sort remains independent
  const [sortBy, setSortBy] = useState<
    "newest" | "price_low" | "price_high" | string
  >("newest");

  // Single source of truth for ALL filters, including category
  const [filters, setFilters] = useState<Filters>({
    condition: undefined,
    size: undefined,
    priceRange: undefined,
    rating: undefined,
    category_subcategory: undefined, // <- category lives here
  });

  // Derived category (no separate state!)
  const selectedCategory = useMemo(
    () => filters.category_subcategory ?? "all",
    [filters.category_subcategory]
  );

  const [isSearching, setIsSearching] = useState(false);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Stable, sanitized params for the query + cache key
  const queryParams = useMemo(
    () => ({
      searchQuery,
      filters: sanitize(filters),
      sortBy,
    }),
    [searchQuery, filters, sortBy]
  );

  // Query for "All Products" section with filters applied
  // NOTE: Temporarily using useProducts instead of useProductsPaginated due to vendor.status filter bug
  const {
    data: filteredProducts,
    isLoading: isLoadingFiltered,
    refetch: refetchProducts,
  } = useProducts(queryParams);
  
  // Pagination disabled while using useProducts (these are dummy values)
  const hasNextPage = false;
  const fetchNextPage = async () => {};
  const isFetchingNextPage = false;
  const serviceableVendorCount = 0;
  
  // Track if we're near the bottom for infinite scroll
  const isLoadingMore = useRef(false);
  const [isAboutToLoadMore, setIsAboutToLoadMore] = useState(false);
  
  // Handle infinite scroll
  const handleScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
    const paddingToBottom = 200; // Load more when 200px from bottom
    
    const isCloseToBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - paddingToBottom;
    
    // Show loading indicator immediately when close to bottom
    if (isCloseToBottom && hasNextPage && !isFetchingNextPage && !isLoadingMore.current) {
      setIsAboutToLoadMore(true);
      isLoadingMore.current = true;
      fetchNextPage().finally(() => {
        isLoadingMore.current = false;
        setIsAboutToLoadMore(false);
      });
    } else if (!isCloseToBottom) {
      setIsAboutToLoadMore(false);
    }
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  // Search input handler
  const handleSearch = useCallback(
    (query: string) => {
      const trimmed = query.trim();
      setIsSearching(Boolean(trimmed));
      setSearchQuery(query);
      if (!trimmed) setIsSearching(false);
    },
    [filteredProducts?.length]
  );

  // Category selection -> only adjust filters.category_subcategory
  const handleCategorySelect = useCallback((category: string) => {
    setFilters((prev) => ({
      ...prev,
      category_subcategory: category === "all" ? undefined : category,
    }));
  }, []);

  // Category picked from a filter pane list
  const handleFilterCategorySelect = useCallback((filterCategory?: string) => {
    setFilters((prev) => ({
      ...prev,
      category_subcategory: filterCategory || undefined,
    }));
  }, []);

  // Accept full filters object, but avoid pointless state updates
  const handleFiltersChange = useCallback((newFilters: Filters) => {
    setFilters((prev) => (shallowEqual(prev, newFilters) ? prev : newFilters));
  }, []);

  // React to route param changes for search ONLY (don't touch category/filters)
  useEffect(() => {
    if (route.params?.searchQuery !== undefined) {
      const q = route.params.searchQuery;
      setSearchQuery(q);
      setIsSearching(Boolean(q.trim()));
    }
  }, [route.params?.searchQuery]);

  // Clear searching flag once loading completes
  useEffect(() => {
    if (!isLoadingFiltered && isSearching) setIsSearching(false);
  }, [isLoadingFiltered, isSearching]);

  // Track whether any filter is active (excluding undefined/null)
  useEffect(() => {
    setIsFilterActive(
      Object.values(filters).some((v) => v !== undefined && v !== null)
    );
  }, [filters]);

  // Listen for global product update events (after checkout)
  useEffect(() => {
    const unsubscribe = appEvents.subscribeToProducts(() => {
      logger.debug('HomeScreen', 'Received product update event, refreshing products...');
      refetchProducts();
    });

    return unsubscribe;
  }, [refetchProducts]);

  // Debug: Log when products data changes
  useEffect(() => {
    if (filteredProducts && filteredProducts.length > 0) {
      logger.debug('HomeScreen', `Products updated: ${filteredProducts.length} products`, {
        sampleQuantities: filteredProducts.slice(0, 3).map((p) => `${p.title}: ${p.quantity}`)
      });
    }
  }, [filteredProducts]);

  // Navigate to product details
  const handleProductPress = useCallback(
    (product: { id: string }) => {
      navigation.navigate("ProductDetails", { id: product.id });
    },
    [navigation]
  );

  // Pull-to-refresh handler for products
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refetchProducts();
    } catch (error) {
      logger.error('HomeScreen', 'Error refreshing products', error);
      // You could add a toast or alert here if desired
      // Alert.alert('Refresh Failed', 'Unable to refresh products. Please try again.');
    } finally {
      setRefreshing(false);
    }
  }, [refetchProducts]);

  // Main content render
  const renderMainContent = useMemo(() => {
    const headerContent = (
      <View style={styles.header}>
        <ProductCategoryList
          selectedCategory={selectedCategory}
          onSelectCategory={handleCategorySelect}
          onFilterCategorySelect={handleFilterCategorySelect}
        />

        <ProductFilterPanel
          filters={filters}
          onFiltersChange={handleFiltersChange}
          sortBy={sortBy}
          onSort={setSortBy}
        />
      </View>
    );

    // VendorShowcase handles its own padding - no wrapper needed
    // This prevents empty space when VendorShowcase returns null
    const vendorShowcase = !searchQuery ? <VendorShowcase /> : null;

    let productsContent;
    if (isLoadingFiltered && !refreshing) {
      // Show loading state only for initial load, not during refresh
      productsContent = (
        <View style={{ paddingTop: spacing.xxl * 2 }}>
          <LoadingState />
        </View>
      );
    } else if (!filteredProducts || filteredProducts.length === 0) {
      productsContent = (
        <EmptyState
          icon={searchQuery ? "search" : "filter"}
          title="No Products Found"
          description={
            searchQuery
              ? "Try adjusting your search terms or filters"
              : "Try adjusting your filters"
          }
        />
      );
    } else {
      productsContent = (
        <ProductGrid
          products={filteredProducts}
          onProductPress={handleProductPress}
        />
      );
    }

    const productsSection = (
      <View style={styles.productsSection}>
        {!searchQuery && (
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Explore</Text>
          </View>
        )}
        {productsContent}
        {/* Loading more indicator - show immediately when about to load or currently loading */}
        {/* Container always takes up space, content shows/hides based on loading state */}
        <View style={[
          styles.loadingMore,
          !isFetchingNextPage && !isAboutToLoadMore && { minHeight: 0, paddingVertical: 0 }
        ]}>
          {(isFetchingNextPage || isAboutToLoadMore) && hasNextPage && (
            <>
              <ActivityIndicator size="small" color={colors.primary} />
              <Text style={styles.loadingMoreText}>Loading more products...</Text>
            </>
          )}
        </View>
        {/* Extra spacer to ensure loading indicator is always visible when scrolling to bottom */}
        <View style={{ height: 40 }} />
      </View>
    );

    return (
      <>
        {headerContent}
        {vendorShowcase}
        {productsSection}
      </>
    );
  }, [
    selectedCategory,
    filters,
    sortBy,
    searchQuery,
    isLoadingFiltered,
    filteredProducts,
    handleCategorySelect,
    handleFilterCategorySelect,
    handleFiltersChange,
    handleProductPress,
    isFetchingNextPage,
    hasNextPage,
    serviceableVendorCount,
    colors,
    styles,
    refreshing,
  ]);

  return (
    <ScrollView
      style={styles.container}
      showsHorizontalScrollIndicator={false}
      bounces
      contentContainerStyle={styles.content}
      removeClippedSubviews
      onScroll={handleScroll}
      scrollEventThrottle={400}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={handleRefresh}
          colors={[colors.primary]}
          tintColor={colors.primary}
          title="Refreshing products..."
          titleColor={colors.textLight}
        />
      }
    >
      <Animated.View style={{ opacity: fadeAnim, transform: [{ translateY: slideAnim }] }}>
        {!searchQuery && canShowOrders && <OngoingOrdersDisplay />}

        <View style={styles.welcomeSection}>
          {!searchQuery ? (
            <>
              <Text style={styles.welcomeGreeting}>
                {`Hello, ${user?.user_metadata?.username || 'Fashionista'}`}
              </Text>
              <Text style={styles.welcomeSubtext}>Discover what's nearby</Text>
            </>
          ) : (
            <>
              <Text style={styles.welcomeGreeting}>Search Results</Text>
              <Text style={styles.welcomeSubtext}>Showing results for "{searchQuery}"</Text>
            </>
          )}
        </View>

        <View style={styles.searchContainer}>
          <SearchBar
            searchQuery={searchQuery}
            setSearchQuery={handleSearch}
            placeholder="Search for products..."
          />
        </View>

        {renderMainContent}
      </Animated.View>
    </ScrollView>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    content: {
      flexGrow: 1,
      paddingBottom: spacing.xl,
    },
    welcomeSection: {
      paddingLeft: spacing.lg + 4,
      paddingRight: spacing.lg,
      paddingTop: spacing.md,
      paddingBottom: spacing.sm,
    },
    welcomeGreeting: {
      fontSize: 28,
      fontWeight: "800",
      color: colors.text,
      letterSpacing: -0.5,
    },
    welcomeSubtext: {
      fontSize: 16,
      color: colors.textSecondary,
      marginTop: 2,
      fontWeight: "500",
    },
    searchContainer: {
      paddingBottom: spacing.sm,
      backgroundColor: colors.background,
      marginBottom: spacing.sm,
    },
    header: {
      backgroundColor: colors.background,
      zIndex: 1,
      paddingTop: spacing.xs,
    },
    productsSection: {
      paddingTop: 0,
    },
    sectionHeader: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      paddingLeft: spacing.lg + 4,
      paddingRight: spacing.lg,
      marginTop: 12,
      marginBottom: 0,
    },
    sectionTitle: {
      fontSize: 22,
      fontWeight: "700",
      color: colors.text,
      letterSpacing: -0.5,
    },
    loadingMore: {
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
      paddingVertical: spacing.xl,
      paddingBottom: spacing.xxl,
      gap: spacing.md,
      minHeight: 100, // Ensure it's always visible and takes up space
      backgroundColor: colors.background, // Make it stand out
    },
    loadingMoreText: {
      fontSize: 15,
      fontWeight: "500",
      color: colors.text,
      marginLeft: spacing.xs,
    },
    endOfList: {
      alignItems: "center",
      paddingVertical: spacing.xl,
      paddingHorizontal: spacing.lg,
    },
    endOfListText: {
      fontSize: 14,
      color: colors.textLight,
      textAlign: "center",
    },
  });
